import { useEffect, useState } from "react"

import { SelectedPriceListItem } from "../../../createOrder/createOrderTypes"
import { DynamicConfigKey, MeetingServiceTabValues } from "../../components/form/formTypes"
import { ReceiptConfirmOrderDialogWrap } from "./ReceiptConfirmOrderDialogWrap"
import { OrderFormStatus } from "../../mainForm/helpers"
import { useCreateOrderForm } from "../../mainForm/CreateOrderFormContext"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { DeadlineMessageDialog } from "./DeadlineMessageDialog"
import {
    DeadlineMessageDisplayType,
    DeadlineMessageQuery,
    DeadlineMessageState,
    fetchDeadlineMessage,
    selectDeadlineMessage,
} from "../../components/shop/delivery/deliveryApi"
import { buildDeadlineMessageParams, hasProductWishDelivery } from "../../components/shop/delivery/helpers"
import { useDeadlineSnackbar } from "../../components/shop/delivery/useDeadlineSnackbar"
import { useCreateOrderDialogType } from "../../hooks/useCreateOrderDialogType"
import { useCanSetProductWishPrice } from "../../hooks/useCanSetProductWishPrice"
import { CreateOrderType } from "../../helpers"

enum DeadlineCheckResult {
    PENDING = "PENDING",
    SHOW_MODAL = "SHOW_MODAL",
    SHOW_SNACKBAR = "SHOW_SNACKBAR",
    PASSED = "PASSED",
}

type DeadlineCheckParams = Readonly<{
    isSpecialOrder: boolean
    isLoading: boolean
    messageInfo: DeadlineMessageState | undefined
}>

function resolveDeadlineCheck({ isSpecialOrder, isLoading, messageInfo }: DeadlineCheckParams): DeadlineCheckResult {
    if (isSpecialOrder) return DeadlineCheckResult.PASSED
    if (isLoading || !messageInfo) return DeadlineCheckResult.PENDING
    if (messageInfo.isConfirmed || !messageInfo.message) return DeadlineCheckResult.PASSED

    switch (messageInfo.displayType) {
        case DeadlineMessageDisplayType.MODAL:
            return DeadlineCheckResult.SHOW_MODAL
        case DeadlineMessageDisplayType.SNACKBAR:
            return DeadlineCheckResult.SHOW_SNACKBAR
        case DeadlineMessageDisplayType.CARD:
            return DeadlineCheckResult.PASSED
    }
}

type ReceiptConfirmOrderDialogControllerProps = Readonly<{
    onClose: () => void
    orderDetails: MeetingServiceTabValues
    handleOpenResourceBooking: (type: CreateOrderType) => void
    handleAddNewMeetingService: () => void
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
}>

export function ReceiptConfirmOrderDialogController({
    onClose,
    orderDetails,
    handleOpenResourceBooking,
    handleAddNewMeetingService,
    orderFormStatusRef,
}: ReceiptConfirmOrderDialogControllerProps) {
    const { user, handleClose, orderContext, setOrderContext, availableModules } = useCreateOrderForm()
    const [showReceiptDialog, setShowReceiptDialog] = useState(false)
    const { isSpecialOrder } = useCreateOrderDialogType()
    const canSetProductWishPrice = useCanSetProductWishPrice()
    const dateTimeFactory = useDateTime()
    const showDeadlineSnackbar = useDeadlineSnackbar()

    const multipleDeliveries = orderDetails[DynamicConfigKey.MULTIPLE_DELIVERIES]

    const priceList: SelectedPriceListItem = orderDetails["priceList"].item!

    const hasProductWish = hasProductWishDelivery(multipleDeliveries)

    // The staff message says "set a price and approve", so it only fits whoever may actually quote.
    const isStaffProductWish = hasProductWish && canSetProductWishPrice

    const deadlineParams = buildDeadlineMessageParams({
        priceList,
        deliveries: multipleDeliveries,
        dateTimeFactory,
    })

    const { data: messageInfo, isLoading } = useApiQuery(fetchDeadlineMessage, {
        queryName: DeadlineMessageQuery.DELIVERY_DEADLINE_MESSAGE,
        dependencies: deadlineParams,
        select: selectDeadlineMessage,
        enabled: !!deadlineParams.deliveryTime && !isSpecialOrder,
    })

    const deadlineCheck = resolveDeadlineCheck({ isSpecialOrder, isLoading, messageInfo })
    const deadlineText = messageInfo?.message?.displayRule

    useEffect(() => {
        if (showReceiptDialog) return
        if (deadlineCheck === DeadlineCheckResult.PENDING || deadlineCheck === DeadlineCheckResult.SHOW_MODAL) return

        if (deadlineCheck === DeadlineCheckResult.SHOW_SNACKBAR && deadlineText) {
            showDeadlineSnackbar(deadlineText, "info")
        }
        setShowReceiptDialog(true)
    }, [deadlineCheck, deadlineText, showReceiptDialog, showDeadlineSnackbar])

    // Show receipt dialog when ready
    if (showReceiptDialog) {
        return (
            <ReceiptConfirmOrderDialogWrap
                orderContext={orderContext}
                setOrderContext={setOrderContext}
                open={showReceiptDialog}
                onClose={onClose}
                handleCloseMainDialog={handleClose}
                handleOpenResourceBooking={handleOpenResourceBooking}
                handleAddNewMeetingService={handleAddNewMeetingService}
                orderDetails={orderDetails}
                mainUser={user}
                orderFormStatusRef={orderFormStatusRef}
                availableModules={availableModules}
            />
        )
    }

    if (!messageInfo?.message || deadlineCheck !== DeadlineCheckResult.SHOW_MODAL) {
        return null
    }

    return (
        <DeadlineMessageDialog
            message={messageInfo.message}
            isStaffProductWish={isStaffProductWish}
            onClose={onClose}
            handlePlaceOrder={() => setShowReceiptDialog(true)}
        />
    )
}
